import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Clock, Trophy, Shuffle, ChevronRight } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

export default function NextRoundPanel({ tid, cats, onGenerated }) {
  const [status, setStatus] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/status`, { credentials: "include", headers: AUTH() });
      if (res.ok) setStatus((await res.json()).categories || []);
    } catch { /* silent */ }
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const slotsForRound = (categoryId, roundNum) => {
    const c = cats.find((x) => x.category_id === categoryId);
    const rs = (c?.round_schedules || []).find((r) => r.round_num === roundNum);
    return rs?.slot_ids || [];
  };

  const generate = async (cat, mode) => {
    const slot_ids = slotsForRound(cat.category_id, cat.next_round);
    if (slot_ids.length === 0) {
      toast.error(`La Ronda ${cat.next_round} no tiene horarios en el calendario de ${cat.category_name}. Configúralos en Convocatoria.`);
      return;
    }
    setBusy(`${cat.category_id}_${mode}`);
    try {
      const body = {
        round_num: cat.next_round, category_id: cat.category_id, slot_ids,
        players_per_group: 4,
        sort_by: mode === "score" ? "by_leaderboard" : "random",
        reverse_order: mode === "score",
      };
      const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/generate`, {
        method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      toast.success(`Ronda ${cat.next_round} de ${cat.category_name} generada (${mode === "score" ? "por score, líderes al último" : "aleatorio"})`);
      load(); onGenerated?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const ready = status.filter((c) => c.next_ready);
  if (ready.length === 0) return null;

  return (
    <div className="mb-4 space-y-2" data-testid="next-round-panel">
      {ready.map((cat) => (
        <div key={cat.category_id} className="flex items-center gap-3 flex-wrap bg-green-50 border border-green-200 rounded-xl px-4 py-3" data-testid={`next-round-${cat.category_id}`}>
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <div className="flex-1 min-w-[180px]">
            <div className="text-sm font-bold text-[#0F172A]">{cat.category_name}</div>
            <div className="text-xs text-[#64748B]">Ronda {cat.next_round - 1} completa ({cat.per_round[cat.next_round - 2].submitted}/{cat.per_round[cat.next_round - 2].total} tarjetas) · lista para generar Ronda {cat.next_round}</div>
          </div>
          <Button size="sm" disabled={busy} onClick={() => generate(cat, "score")} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid={`gen-next-score-${cat.category_id}`}>
            <Trophy className="w-3.5 h-3.5 mr-1" /> Por score
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => generate(cat, "random")} data-testid={`gen-next-random-${cat.category_id}`}>
            <Shuffle className="w-3.5 h-3.5 mr-1" /> Aleatorio
          </Button>
        </div>
      ))}
    </div>
  );
}

export function RoundProgressBadges({ tid }) {
  const [status, setStatus] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/status`, { credentials: "include", headers: AUTH() });
        if (res.ok) setStatus((await res.json()).categories || []);
      } catch { /* silent */ }
    })();
  }, [tid]);
  if (status.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="round-progress">
      {status.map((c) => c.per_round.filter((r) => r.has_teetimes || r.submitted > 0).map((r) => (
        <span key={`${c.category_id}-${r.round_num}`} className={`text-[11px] px-2 py-1 rounded-full flex items-center gap-1 ${r.complete ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
          {r.complete ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {c.category_name} R{r.round_num}: {r.submitted}/{r.total}
        </span>
      )))}
    </div>
  );
}
